import { useEffect, useState, useMemo } from "react";
import client from "../api/client";
import { Download } from "lucide-react";
import "./Employees.css";
import { useMasterPins } from "../context/PinContext";
import { getEmployeeIdForProtectedAction } from "../utils/masterPinActions";
import { logEmployeeActivity } from "../utils/activityLogger";

const formatCurrency = (value) => {
  const number = Number(value || 0);
  if (Number.isNaN(number)) return "₹ 0.00";
  return `₹ ${number.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
};

const formatDateForInput = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (value) => formatDateForInput(value) || "-";

export default function Employees() {
  const { verifyPinOverride } = useMasterPins();
  const [employees, setEmployees] = useState([]);
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    email: "",
    password: "",
    role: "Employee",
    base_salary: "",
    joining_date: "",
    salary_type: "fixed",
    weekly_off: "",
  });

  const [editEmployee, setEditEmployee] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedPinEmployeeId, setSelectedPinEmployeeId] = useState("");
  const [generatedPin, setGeneratedPin] = useState("");
  const [isGeneratingPin, setIsGeneratingPin] = useState(false);
  const itemsPerPage = 20;
  const [showBaseSalaryPin, setShowBaseSalaryPin] = useState(false);
  const [baseSalaryPin, setBaseSalaryPin] = useState("");
  const [baseSalaryPinError, setBaseSalaryPinError] = useState("");
  const [isBaseSalaryUnlocked, setIsBaseSalaryUnlocked] = useState(false);
  const employeeId = getEmployeeIdForProtectedAction();

  // Fetch employees
  const fetchEmployees = async () => {
    try {
      const res = await client.get("/users");
      setEmployees(res.data || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
      alert("Failed to fetch employees");
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleChange = (e) => {
    setNewEmployee({ ...newEmployee, [e.target.name]: e.target.value });
  };

  // Add employee
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newEmployee.name || !newEmployee.email || !newEmployee.password) {
      alert("Name, Email & Password required");
      return;
    }

    try {
      const payload = {
        ...newEmployee,
        base_salary: Number(newEmployee.base_salary) || 0,
      };
      await client.post("/users", payload, {
        headers: { "Content-Type": "application/json" },
      });
      void logEmployeeActivity({
        action: "Employee Created",
        module: "Employees",
        description: `Created employee "${payload.name}" with role ${payload.role}.`,
      });

      setNewEmployee({
        name: "",
        email: "",
        password: "",
        role: "Employee",
        base_salary: "",
        joining_date: "",
        salary_type: "fixed",
        weekly_off: "",
      });
      fetchEmployees();
      alert("Employee added successfully");
    } catch (err) {
      console.error("Error adding employee:", err);
      alert("Failed to add employee");
    }
  };

  // Delete employee
  const deleteEmployee = async (id) => {
    if (!confirm("Delete this employee?")) return;
    const employeeToDelete = employees.find((employee) => employee.id === id);
    try {
      await client.delete(`/users/${id}`);
      void logEmployeeActivity({
        targetEmployeeId: id,
        targetEmployeeName: employeeToDelete?.name || `Employee #${id}`,
        action: "Employee Deleted",
        module: "Employees",
        description: `Deleted employee "${employeeToDelete?.name || `Employee #${id}`}".`,
      });
      fetchEmployees();
    } catch (err) {
      console.error("Error deleting employee:", err);
      alert("Failed to delete employee");
    }
  };

  // Edit bar open
const openEditBar = (emp) => {
  setEditEmployee({
    id: emp.id,
    name: emp.name || "",
    email: emp.email || "",
    role: emp.role || "Employee",
    base_salary: emp.base_salary ?? "",
    joining_date: formatDateForInput(emp.joining_date),
    salary_type: emp.salary_type === "attendance_based" || emp.salary_type === "daily" ? "attendance_based" : "fixed",
    weekly_off: emp.weekly_off || "",
    password: "",
  });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEditChange = (e) => {
    setEditEmployee({ ...editEmployee, [e.target.name]: e.target.value });
  };

  // Update employee
  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!editEmployee.name || !editEmployee.email) {
      alert("Name and email required");
      return;
    }

    const payload = {
      name: editEmployee.name,
      email: editEmployee.email,
      role: editEmployee.role,
      joining_date: editEmployee.joining_date || null,
      salary_type: editEmployee.salary_type || "fixed",
      weekly_off: editEmployee.weekly_off || null,
    };

    if (editEmployee.password?.trim()) {
      payload.password = editEmployee.password;
    }
    if (editEmployee.base_salary !== undefined) {
      payload.base_salary = Number(editEmployee.base_salary) || 0;
    }

    try {
      await client.put(`/users/${editEmployee.id}`, payload, {
        headers: { "Content-Type": "application/json" },
      });
      void logEmployeeActivity({
        targetEmployeeId: editEmployee.id,
        targetEmployeeName: editEmployee.name,
        action: "Employee Updated",
        module: "Employees",
        description: `Updated employee "${editEmployee.name}".`,
      });

      setEditEmployee(null);
      fetchEmployees();
      alert("Employee updated");
    } catch (err) {
      console.error("Error updating employee:", err);
      alert("Failed to update employee");
    }
  };

  // Export to Excel (placeholder)
  const exportToExcel = () => {
    alert("Export to Excel functionality will be implemented");
  };

  const openPinModal = () => {
    setGeneratedPin("");
    setSelectedPinEmployeeId("");
    setShowPinModal(true);
  };

  const closePinModal = () => {
    setShowPinModal(false);
    setSelectedPinEmployeeId("");
    setGeneratedPin("");
  };

  const generatePinForEmployee = async () => {
    if (!selectedPinEmployeeId) {
      alert("Please select an employee");
      return;
    }

    setIsGeneratingPin(true);
    setGeneratedPin("");

    try {
      const payload = { employeeId: Number(selectedPinEmployeeId) };
      let res;

      try {
        res = await client.post("/users/generate-pin", payload);
      } catch (err) {
        if (err?.response?.status === 404) {
          // Backward compatibility for servers where this route is wired under /auth
          res = await client.post("/auth/generate-pin", payload);
        } else {
          throw err;
        }
      }

      if (!res?.data?.pin) {
        throw new Error("PIN not returned by API");
      }

      setGeneratedPin(res.data.pin);
      const selectedEmployee = employees.find(
        (employee) => String(employee.id) === String(selectedPinEmployeeId)
      );
      void logEmployeeActivity({
        targetEmployeeId: Number(selectedPinEmployeeId),
        targetEmployeeName:
          selectedEmployee?.name || `Employee #${selectedPinEmployeeId}`,
        action: "Login PIN Generated",
        module: "Employees",
        description: `Generated login PIN for ${selectedEmployee?.name || `Employee #${selectedPinEmployeeId}`}.`,
      });
    } catch (err) {
      console.error("PIN generation error:", err);
      const status = err?.response?.status;
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (status ? `Failed to generate PIN (HTTP ${status})` : "Failed to generate PIN");
      alert(message);
    } finally {
      setIsGeneratingPin(false);
    }
  };

  // Pagination
  const totalPages = Math.ceil(employees.length / itemsPerPage);
  const paginatedEmployees = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return employees.slice(start, start + itemsPerPage);
  }, [employees, currentPage]);

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, employees.length);

  return (
    <div className="employees-page page-container">
      {/* Header */}
      <header className="employees-header">
        <div className="header-left">
          <h1 className="header-title">Staff Management</h1>
          <p className="header-subtitle">Add and manage your organization's employees and their roles.</p>
        </div>
        <div className="header-actions">
          <button type="button" className="btn btn-generate-pin" onClick={openPinModal}>
            <span>Generate PIN</span>
          </button>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => {
              setShowBaseSalaryPin(true);
              setBaseSalaryPin("");
              setBaseSalaryPinError("");
            }}
          >
            Unlock Base Salary
          </button>
          <button type="button" className="btn btn-export" onClick={exportToExcel}>
            <Download size={18} strokeWidth={2} />
            <span>Export to Excel</span>
          </button>
          <div className="total-staff-badge">
            Total Staff: {employees.length}
          </div>
        </div>
      </header>

      {showPinModal && (
        <div className="pin-modal-overlay" onClick={closePinModal}>
          <div className="pin-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="pin-modal-header">
              <h3>Generate Employee PIN</h3>
              <button type="button" className="pin-modal-close" onClick={closePinModal}>
                X
              </button>
            </div>

            <div className="pin-modal-body">
              <label className="field-label">SELECT EMPLOYEE</label>
              <select
                className="form-input"
                value={selectedPinEmployeeId}
                onChange={(e) => setSelectedPinEmployeeId(e.target.value)}
              >
                <option value="">-- Select Employee --</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name} {emp.email ? `(${emp.email})` : ""}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="btn btn-generate-pin-submit"
                onClick={generatePinForEmployee}
                disabled={isGeneratingPin}
              >
                {isGeneratingPin ? "Generating..." : "Generate PIN"}
              </button>

              {generatedPin ? (
                <div className="generated-pin-box">
                  Employee PIN Generated: <strong>{generatedPin}</strong>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
      {showBaseSalaryPin && (
        <div className="pin-modal-overlay" onClick={() => setShowBaseSalaryPin(false)}>
          <div className="pin-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="pin-modal-header">
              <h3>Unlock Base Salaries</h3>
              <button type="button" className="pin-modal-close" onClick={() => setShowBaseSalaryPin(false)}>
                X
              </button>
            </div>
            <div className="pin-modal-body">
              <p className="pin-modal-description">Enter the 4-digit PIN to reveal base salary values.</p>
              <form
                className="salary-unlock-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  let isOverrideValid = false;

                  try {
                    isOverrideValid = Boolean(
                      employeeId &&
                        (await verifyPinOverride(
                          employeeId,
                          "employee_base_salary_unlock",
                          baseSalaryPin
                        ))
                    );
                  } catch (error) {
                    console.error("Base salary PIN verification failed:", error);
                  }

                  if (isOverrideValid) {
                    void logEmployeeActivity({
                      action: "Base Salary Unlocked",
                      module: "Employees",
                      description: "Unlocked employee base salary values.",
                    });
                    setIsBaseSalaryUnlocked(true);
                    setBaseSalaryPin("");
                    setBaseSalaryPinError("");
                    setShowBaseSalaryPin(false);
                  } else {
                    setBaseSalaryPinError("Incorrect PIN");
                  }
                }}
              >
                <label className="salary-otp-field">
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    value={baseSalaryPin}
                    onChange={(event) =>
                      setBaseSalaryPin(event.target.value.replace(/\D/g, "").slice(0, 4))
                    }
                    className="salary-lock-input"
                    placeholder="4-digit PIN"
                    required
                  />
                  <div className="salary-otp-boxes" aria-hidden="true">
                    {[0, 1, 2, 3].map((index) => (
                      <span key={index} className={`salary-otp-box ${baseSalaryPin[index] ? "filled" : ""}`}>
                        {baseSalaryPin[index] || ""}
                      </span>
                    ))}
                  </div>
                </label>
                <button type="submit" className="btn btn-primary">
                  Unlock
                </button>
              </form>
              {baseSalaryPinError && <div className="salary-lock-error">{baseSalaryPinError}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Edit Bar */}
      {editEmployee && (
        <form onSubmit={handleUpdate} className="edit-bar">
          <input
            type="text"
            name="name"
            value={editEmployee.name}
            onChange={handleEditChange}
            required
          />

          <input
            type="email"
            name="email"
            value={editEmployee.email}
            onChange={handleEditChange}
            required
          />

          <select
            name="role"
            value={editEmployee.role}
            onChange={handleEditChange}
            required
          >
            <option value="Employee">Employee</option>
            <option value="TL">TL</option>
            <option value="SubAdmin">SubAdmin</option>
            <option value="Admin">Admin</option>
          </select>

          <input
            type="number"
            name="base_salary"
            value={editEmployee.base_salary || ""}
            onChange={handleEditChange}
            placeholder="Base Salary (₹)"
            min="0"
            step="0.01"
            className="form-input"
          />

          <input
            type="date"
            name="joining_date"
            value={editEmployee.joining_date || ""}
            onChange={handleEditChange}
            className="form-input"
          />

          <select
            name="salary_type"
            value={editEmployee.salary_type || "fixed"}
            onChange={handleEditChange}
            className="form-input"
          >
            <option value="fixed">Fixed</option>
            <option value="attendance_based">Attendance Based</option>
          </select>

          <select
            name="weekly_off"
            value={editEmployee.weekly_off || ""}
            onChange={handleEditChange}
            className="form-input"
          >
            <option value="">Weekly Off</option>
            <option value="Sunday">Sunday</option>
            <option value="Monday">Monday</option>
            <option value="Tuesday">Tuesday</option>
            <option value="Wednesday">Wednesday</option>
            <option value="Thursday">Thursday</option>
            <option value="Friday">Friday</option>
            <option value="Saturday">Saturday</option>
          </select>

          <input
            type="password"
            name="password"
            value={editEmployee.password}
            onChange={handleEditChange}
            placeholder="New password (optional)"
          />

          <button type="submit" className="save-btn">Update</button>
          <button
            type="button"
            className="cancel-btn"
            onClick={() => setEditEmployee(null)}
          >
            Cancel
          </button>
        </form>
      )}

      {/* Main Card */}
      <div className="employees-main-card">
        {/* ADD NEW EMPLOYEE Section */}
        <div className="add-employee-section">
          <h2 className="section-title">ADD NEW EMPLOYEE</h2>
          <form onSubmit={handleSubmit} className="add-employee-form">
            <div className="form-field">
              <label className="field-label">FULL NAME</label>
              <input
                type="text"
                name="name"
                placeholder="e.g. Rahul Sharma"
                value={newEmployee.name}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>

            <div className="form-field">
              <label className="field-label">EMAIL ADDRESS</label>
              <input
                type="email"
                name="email"
                placeholder="rahul@example.com"
                value={newEmployee.email}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>

            <div className="form-field">
              <label className="field-label">PASSWORD</label>
              <input
                type="password"
                name="password"
                placeholder="********"
                value={newEmployee.password}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>

          <div className="form-field">
            <label className="field-label">ACCESS ROLE</label>
            <select
              name="role"
              value={newEmployee.role}
              onChange={handleChange}
              className="form-input"
              required
            >
                <option value="Employee">Employee</option>
                <option value="TL">TL</option>
                <option value="SubAdmin">SubAdmin</option>
                <option value="Admin">Admin</option>
              </select>
            </div>

            <div className="form-field">
              <label className="field-label">BASE SALARY (₹)</label>
              <input
                type="number"
                name="base_salary"
                value={newEmployee.base_salary}
                onChange={handleChange}
                className="form-input"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-field">
              <label className="field-label">JOINING DATE</label>
              <input
                type="date"
                name="joining_date"
                value={newEmployee.joining_date}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-field">
              <label className="field-label">SALARY TYPE</label>
              <select
                name="salary_type"
                value={newEmployee.salary_type}
                onChange={handleChange}
                className="form-input"
              >
                <option value="fixed">Fixed</option>
                <option value="attendance_based">Attendance Based</option>
              </select>
            </div>

            <div className="form-field">
              <label className="field-label">WEEKLY OFF</label>
              <select
                name="weekly_off"
                value={newEmployee.weekly_off}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">Select Day</option>
                <option value="Sunday">Sunday</option>
                <option value="Monday">Monday</option>
                <option value="Tuesday">Tuesday</option>
                <option value="Wednesday">Wednesday</option>
                <option value="Thursday">Thursday</option>
                <option value="Friday">Friday</option>
                <option value="Saturday">Saturday</option>
              </select>
            </div>

            <div className="form-field form-field-button">
              <button type="submit" className="btn btn-add-employee">
                ADD EMPLOYEE
              </button>
            </div>
          </form>
        </div>

        {/* Employee Table */}
        <div className="table-section">
          <div className="table-wrapper">
            <table className="employee-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>NAME</th>
                  <th>EMAIL</th>
                  <th>ROLE</th>
                  <th>JOINING DATE</th>
                  <th>SALARY TYPE</th>
                  <th>WEEKLY OFF</th>
                  <th>BASE SALARY</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>

              <tbody>
                {paginatedEmployees.length > 0 ? (
                  paginatedEmployees.map((emp) => (
                    <tr key={emp.id}>
                      <td className="id-cell">#{emp.id}</td>
                      <td>{emp.name}</td>
                      <td>{emp.email}</td>
                      <td>
                        <span className={`role-badge role-${emp.role?.toLowerCase() || "employee"}`}>
                          {emp.role || "Employee"}
                        </span>
                      </td>
                      <td>{formatDateForDisplay(emp.joining_date)}</td>
                      <td>{emp.salary_type === "attendance_based" || emp.salary_type === "daily" ? "Attendance Based" : "Fixed"}</td>
                      <td>{emp.weekly_off || "-"}</td>
                      <td>
                        {isBaseSalaryUnlocked ? (
                          <span>{formatCurrency(emp.base_salary)}</span>
                        ) : (
                          <span className="locked-text">******</span>
                        )}
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button
                            type="button"
                            className="action-link action-edit"
                            onClick={() => openEditBar(emp)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="action-link action-delete"
                            onClick={() => deleteEmployee(emp.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="empty-row">
                      No employees found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {employees.length > 0 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {startIndex} to {endIndex} of {employees.length} employees
              </div>
              <div className="pagination-controls">
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      type="button"
                      className={`pagination-btn ${currentPage === page ? "active" : ""}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
